/**
 * RecordingUploadStore
 *
 * Zustand store for tracking the active S3 upload progress.
 * Decoupled from the recording session because the upload begins
 * after recording ends and must persist independently.
 *
 * Consumed by: Upload overlay bar, list view (uploading card),
 * beforeunload handler, recovery prompt (disable upload action).
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useMemo } from 'react';

// ─── State ──────────────────────────────────────────────────────────

interface RecordingUploadState {
  /** Matter this upload belongs to */
  matterId: number | null;
  /** Recording ID being uploaded */
  recordingId: number | null;
  /** Recording title for display in overlay */
  recordingTitle: string | null;

  /** Whether an upload is in progress */
  isUploading: boolean;
  /** Bytes uploaded so far */
  bytesUploaded: number;
  /** Total bytes to upload */
  bytesTotal: number;
  /** Percentage complete (0-100) */
  percentComplete: number;
  /** Estimated seconds remaining (null if not calculable yet) */
  etaSeconds: number | null;

  /** Whether the upload has failed after retries */
  uploadFailed: boolean;
  /**
   * Human-readable reason for the last failure (from `UploadResult.error`),
   * shown as a supplementary detail line alongside the generic failed copy.
   * Null when there's no failure or the caller didn't pass one.
   */
  uploadFailedReason: string | null;
  /** Number of retry attempts */
  retryCount: number;
  /** Whether the device is offline */
  isOffline: boolean;
}

// ─── Actions ────────────────────────────────────────────────────────

interface RecordingUploadActions {
  /** Start tracking a new upload */
  startUpload: (
    matterId: number,
    recordingId: number,
    title: string,
    totalBytes: number
  ) => void;
  /**
   * Update only the totalBytes after upload has started (e.g. when chunk
   * assembly completes and the true size is known). Doesn't reset retryCount
   * or progress samples — safe to call mid-upload and during retry.
   */
  setTotalBytes: (totalBytes: number) => void;
  /** Update upload progress from XHR onprogress event */
  updateProgress: (bytesUploaded: number) => void;
  /** Mark the upload as failed, optionally with a user-facing reason */
  setFailed: (reason?: string) => void;
  /** Update offline status */
  setOffline: (offline: boolean) => void;
  /** Retry the upload (increments retry count) */
  retry: () => void;
  /** Mark the upload as complete */
  complete: () => void;
  /** Reset all state */
  reset: () => void;
}

type RecordingUploadStore = RecordingUploadState & RecordingUploadActions;

// ─── Initial State ──────────────────────────────────────────────────

const initialState: RecordingUploadState = {
  matterId: null,
  recordingId: null,
  recordingTitle: null,
  isUploading: false,
  bytesUploaded: 0,
  bytesTotal: 0,
  percentComplete: 0,
  etaSeconds: null,
  uploadFailed: false,
  uploadFailedReason: null,
  retryCount: 0,
  isOffline: false
};

// ─── ETA Tracking ───────────────────────────────────────────────────

/** Sliding window for ETA calculation (last 5 seconds of progress) */
let progressSamples: { timestamp: number; bytes: number }[] = [];

function calculateEta(
  bytesUploaded: number,
  bytesTotal: number
): number | null {
  const now = Date.now();
  progressSamples.push({ timestamp: now, bytes: bytesUploaded });

  // Keep only samples from the last 5 seconds
  const cutoff = now - 5000;
  progressSamples = progressSamples.filter((s) => s.timestamp >= cutoff);

  if (progressSamples.length < 2) return null;

  const oldest = progressSamples[0];
  const elapsed = (now - oldest.timestamp) / 1000;
  if (elapsed < 0.5) return null;

  const bytesPerSecond = (bytesUploaded - oldest.bytes) / elapsed;
  if (bytesPerSecond <= 0) return null;

  const remaining = bytesTotal - bytesUploaded;
  return Math.round(remaining / bytesPerSecond);
}

// ─── Store ──────────────────────────────────────────────────────────

export const useRecordingUploadStore = create<RecordingUploadStore>()(
  devtools(
    (set) => ({
      ...initialState,

      startUpload: (matterId, recordingId, title, totalBytes) => {
        progressSamples = [];
        set(
          {
            matterId,
            recordingId,
            recordingTitle: title,
            isUploading: true,
            bytesUploaded: 0,
            bytesTotal: totalBytes,
            percentComplete: 0,
            etaSeconds: null,
            uploadFailed: false,
            uploadFailedReason: null,
            retryCount: 0,
            isOffline: false
          },
          false,
          'startUpload'
        );
      },

      setTotalBytes: (totalBytes) => {
        set(
          (state) => {
            const percentComplete =
              totalBytes > 0
                ? Math.round((state.bytesUploaded / totalBytes) * 100)
                : 0;
            return { bytesTotal: totalBytes, percentComplete };
          },
          false,
          'setTotalBytes'
        );
      },

      updateProgress: (bytesUploaded) => {
        set(
          (state) => {
            const percentComplete =
              state.bytesTotal > 0
                ? Math.round((bytesUploaded / state.bytesTotal) * 100)
                : 0;
            const etaSeconds = calculateEta(bytesUploaded, state.bytesTotal);

            return { bytesUploaded, percentComplete, etaSeconds };
          },
          false,
          'updateProgress'
        );
      },

      setFailed: (reason) => {
        set(
          {
            uploadFailed: true,
            isUploading: false,
            uploadFailedReason: reason ?? null
          },
          false,
          'setFailed'
        );
      },

      setOffline: (offline) => {
        set({ isOffline: offline }, false, 'setOffline');
      },

      retry: () => {
        progressSamples = [];
        set(
          (state) => ({
            retryCount: state.retryCount + 1,
            uploadFailed: false,
            uploadFailedReason: null,
            isUploading: true,
            bytesUploaded: 0,
            percentComplete: 0,
            etaSeconds: null
          }),
          false,
          'retry'
        );
      },

      complete: () => {
        set({ isUploading: false, percentComplete: 100 }, false, 'complete');
      },

      reset: () => {
        progressSamples = [];
        set(initialState, false, 'reset');
      }
    }),
    {
      name: 'RecordingUploadStore',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
);

// ─── Selector Hooks ─────────────────────────────────────────────────

export const useUploadRecordingId = () =>
  useRecordingUploadStore((s) => s.recordingId);

// ─── Non-Hook Getters ───────────────────────────────────────────────

export const getRecordingUploadActions = () => ({
  startUpload: useRecordingUploadStore.getState().startUpload,
  setTotalBytes: useRecordingUploadStore.getState().setTotalBytes,
  updateProgress: useRecordingUploadStore.getState().updateProgress,
  setFailed: useRecordingUploadStore.getState().setFailed,
  setOffline: useRecordingUploadStore.getState().setOffline,
  retry: useRecordingUploadStore.getState().retry,
  complete: useRecordingUploadStore.getState().complete,
  reset: useRecordingUploadStore.getState().reset
});

export const getRecordingUploadState = () => useRecordingUploadStore.getState();

/** Memoized action hook for stable reference inside components */
export const useRecordingUploadActions = () => {
  return useMemo(() => getRecordingUploadActions(), []);
};
